import { HYDRATE } from "next-redux-wrapper";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TimeSort } from "@components/Features/catalog/catalog-utils";
import { optimizeFiltersFn } from "@features/filters/optimizeFilters";
import { City } from "@shared/lib/interfaces/city.interface";
import { FiltersState } from "./storeTypes";

const initialState = {
    previewQnt: null,
    catalogTimeSort: TimeSort.AllTime,
    price: "",
    color: "",
    city: [],
    mainScreenTimeSort: TimeSort.Week
} as FiltersState;

const filtersSlice = createSlice({
    name: "filters",
    initialState,
    reducers: {
        setBrand(state, action) {
            const newBrand = action.payload;
            if (!newBrand || !state.brand || state.brand.id !== newBrand.id) {
                state.model = null;
                state.generation = null;
            }
            state.brand = newBrand;
        },
        setModel(state, action) {
            const newModel = action.payload;
            if (!newModel || !state.model || state.model.id !== newModel.id) {
                state.generation = null;
            }
            state.model = newModel;
        },
        setGeneration: (state, action) => {
            state.generation = action.payload;
        },
        setYearFrom(state, action) {
            state.yearFrom = action.payload;
        },
        setYearTo(state, action) {
            state.yearTo = action.payload;
        },
        setBody: (state, action) => {
            state.body = action.payload;
        },
        setTransmission: (state, action) => {
            state.transmission = action.payload;
        },
        setEngineType: (state, action) => {
            state.engineType = action.payload;
        },
        setDrive: (state, action) => {
            state.drive = action.payload;
        },
        setPriceFrom: (state, action) => {
            state.priceFrom = action.payload;
        },
        setPriceTo: (state, action) => {
            state.priceTo = action.payload;
        },
        setMileageFrom: (state, action) => {
            state.mileageFrom = action.payload;
        },
        setMileageTo: (state, action) => {
            state.mileageTo = action.payload;
        },
        setHorsePowerFrom: (state, action) => {
            state.horsePowerFrom = action.payload;
        },
        setHorsePowerTo: (state, action) => {
            state.horsePowerTo = action.payload;
        },
        setWheel: (state, action) => {
            state.wheel = action.payload;
        },
        setOwnersQntFrom: (state, action) => {
            state.ownersQntFrom = action.payload;
        },
        setOwnersQntTo: (state, action) => {
            state.ownersQntTo = action.payload;
        },
        setCreatedAt: (state, action) => {
            state.createdAt = action.payload;
        },
        setCities: (state, action: PayloadAction<City[] | undefined>) => {
            const payload = action.payload;
            if (!payload || payload.length === 0 || (payload.length === 1 && !payload[0].alias)) {
                state.city = [];
                return;
            }
            state.city = payload;
        },
        setCityCenterCoords: (state, action) => {
            state.cityCenterCoords = action.payload;
        },
        setSource(state, action) {
            state.sourceId = action.payload;
        },
        setCountry(state, action) {
            state.country = action.payload;
        },
        setSeller(state, action) {
            state.sellerType = action.payload;
        },
        setEngineVolumeFrom(state, action) {
            state.engineVolumeFrom = action.payload;
        },
        setEngineVolumeTo(state, action) {
            state.engineVolumeTo = action.payload;
        },
        setHideNoImages: (state, action: PayloadAction<boolean>) => {
            state.hideNoImages = action.payload;
        },
        optimizeFilters: (state) => {
            optimizeFiltersFn(state);
        },
        clearFilters: (state) => {
            state.brand = undefined;
            state.model = undefined;
            state.yearFrom = undefined;
            state.yearTo = undefined;
            state.priceFrom = undefined;
            state.priceTo = undefined;
            state.price = "";
            state.transmission = "";
            state.drive = "";
            state.body = "";
            state.color = "";
            state.city = [];
            state.cityCenterCoords = undefined;
            state.engineType = "";
            state.generation = undefined;
            state.horsePowerFrom = undefined;
            state.horsePowerTo = undefined;
            state.wheel = undefined;
            state.ownersQntFrom = undefined;
            state.ownersQntTo = undefined;
            state.sellerType = undefined;
            state.sourceId = undefined;
            state.country = undefined;
            state.engineVolumeFrom = undefined;
            state.engineVolumeTo = undefined;
            state.mileageFrom = undefined;
            state.mileageTo = undefined;
            state.catalogTimeSort = TimeSort.AllTime;
            state.hideNoImages = undefined;
        },

        setMainScreenTimeSort: (state, action) => {
            state.mainScreenTimeSort = action.payload;
        },
        setMainScreenParamsSort: (state, action) => {
            state.mainScreenAllParamsSort = action.payload;
        },
        setCatalogTimeSort: (state, action) => {
            state.catalogTimeSort = action.payload;
        },
        setCatalogParamsSort: (state, action) => {
            state.catalogAllParamsSort = action.payload;
        }
    },
    extraReducers: {
        [HYDRATE]: (state, action) => {
            return (state = {
                ...state,
                ...action.payload.filters
            });
        }
    }
});

export const {
    setBrand,
    setModel,
    setYearFrom,
    setYearTo,
    setBody,
    setTransmission,
    setEngineType,
    setDrive,
    setGeneration,
    setPriceFrom,
    setPriceTo,
    setMileageFrom,
    setMileageTo,
    setHorsePowerFrom,
    setHorsePowerTo,
    setWheel,
    setOwnersQntFrom,
    setOwnersQntTo,
    clearFilters,
    setCities,
    setSource,
    setCountry,
    setSeller,
    setCreatedAt,
    optimizeFilters,
    setEngineVolumeFrom,
    setEngineVolumeTo,
    setMainScreenTimeSort,
    setMainScreenParamsSort,
    setCatalogParamsSort,
    setCatalogTimeSort,
    setCityCenterCoords,
    setHideNoImages
} = filtersSlice.actions;

export default filtersSlice;